import { Component } from '@angular/core';
import { TableComponent } from '@shared/components/table/table.component';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { meteringPointHistoryTable } from '@shared/table-configs/meteringpoint/meteringpoint-history.table';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { MeteringPointHistoricalDto, MeteringPointService } from '@app/api/dmc';
import { TableCellComponent } from '@shared/components/table/table-cell/table-cell.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TabsComponent } from '@shared/components/tabs/tabs.component';
import { TabComponent } from '@shared/components/tabs/tab/tab.component';
import {
  locationDetailsColumns,
  meterDetailsColumns,
  meteringDetailsColumns,
  physicalDetailsColumns
} from '@shared/columns/metering-historical.coloumns';
import { Columns } from '@shared/interfaces/table/column-config';
import { NgClass } from '@angular/common';

interface DetailTab {
  title: string;
  columns: Columns<any>[];
}

@Component({
  selector: 'app-meteringpoint-history',
  imports: [
    TableComponent,
    TableCellComponent,
    TranslatePipe,
    TabsComponent,
    TabComponent,
    NgClass
  ],
  templateUrl: './meteringpoint-history.component.html',
  styleUrl: './meteringpoint-history.component.css'
})
export class MeteringpointHistoryComponent {
  /** Selected Elements **/
  public selectedElements: any[] = [];

  /** Table Config **/
  public historyTableConfig: TableConfig<MeteringPointHistoricalDto> = {
    ...meteringPointHistoryTable,
    refreshable: true,
    refresh: () => this.fetchHistory(),
  };

  /** Table Data **/
  public historyTableData: MeteringPointHistoricalDto[] = [];

  /** ID **/
  public id?: string;

  /** Detail Tabs Configuration **/
  public detailTabs: DetailTab[] = [
    {
      title: 'meteringPoint.historical.tabs.physicalDetails',
      columns: physicalDetailsColumns
    },
    {
      title: 'meteringPoint.historical.tabs.locationDetails',
      columns: locationDetailsColumns
    },
    {
      title: 'meteringPoint.historical.tabs.meteringDetails',
      columns: meteringDetailsColumns
    },
    {
      title: 'meteringPoint.historical.tabs.meterDetails',
      columns: meterDetailsColumns
    }
  ];

  constructor(
    private readonly meteringPointService: MeteringPointService,
    private readonly route: ActivatedRoute,
  ) {
    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;
      this.fetchHistory();
    });
  }

  public fetchHistory(): void {
    if (!this.id) return;
    this.meteringPointService.meteringPointGethistoricalGet(this.id).subscribe((data: MeteringPointHistoricalDto[]) => {
      this.historyTableData = data ?? [];
    });
  }

  /**
   * Check if a value has changed compared to the previous element
   * @param colKey - The column key to check
   * @param currentIndex - Index of the current element
   * @returns true if the value changed from the previous element
   */
  public hasValueChanged(colKey: string | number | symbol, currentIndex: number): boolean {
    if (currentIndex === 0) {
      return false; // First element has nothing to compare to
    }

    const currentValue = this.selectedElements[currentIndex][colKey];
    const previousValue = this.selectedElements[currentIndex - 1][colKey];

    // Handle null/undefined comparisons
    if (currentValue === previousValue) {
      return false;
    }

    // Handle dates
    if (currentValue instanceof Date && previousValue instanceof Date) {
      return currentValue.getTime() !== previousValue.getTime();
    }

    // Handle objects (deep comparison might be needed depending on your data)
    if (typeof currentValue === 'object' && typeof previousValue === 'object') {
      return JSON.stringify(currentValue) !== JSON.stringify(previousValue);
    }

    return currentValue !== previousValue;
  }
}
